const DEFAULT_ASSUMPTIONS = {
  dieselInefficiencyPerPsiPct: 0.2,
  tireWearPerPsiPct: 0.35,
  sensorLifetimeYears: 7,
  simpleInvestmentUsd: 1950,
  fullInvestmentUsd: 3750,
  simpleMonthlyUsd: 40,
  fullMonthlyUsd: 80,
  tractorTires: 10,
  withApsFactor: 0.2,
};

const num = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const safeDiv = (a, b) => (Math.abs(b) < 1e-9 ? 0 : a / b);
const round = (value, digits = 2) => Number(value.toFixed(digits));

const toCurrency = (usdValue, currency, exchangeRate) => (
  currency === 'USD' ? usdValue : usdValue * exchangeRate
);

export const calculateClientSavings = (input) => {
  const assumptions = { ...DEFAULT_ASSUMPTIONS, ...(input.assumptions || {}) };

  const tractos = num(input.tractos, 1);
  const rem2 = num(input.trailers_2axle_8tires, 0);
  const rem3 = num(input.trailers_3axle_12tires, 0);
  const configType = input.configuration_type || 'Sencillo';
  const pctFull = Math.min(Math.max(num(input.full_units_percentage, 0), 0), 100);

  let fullUnits = 0;
  let simpleUnits = tractos;
  if (configType === 'Full') {
    fullUnits = tractos;
    simpleUnits = 0;
  }
  if (configType === 'Mixto') {
    fullUnits = tractos * (pctFull / 100);
    simpleUnits = Math.max(tractos - fullUnits, 0);
  }

  const autoTotalTires = (tractos * assumptions.tractorTires) + (rem2 * 8) + (rem3 * 12);
  const totalTires = num(input.total_tires, autoTotalTires) > 0 ? num(input.total_tires, autoTotalTires) : autoTotalTires;

  const standardPsi = num(input.recommended_psi, 100);
  const actualPsi = num(input.actual_avg_psi, 94);
  const psiDifference = Math.max(standardPsi - actualPsi, 0);

  const kmPerUnitMonth = num(input.monthly_km_per_unit, 15000);
  const kmLsimple = Math.max(num(input.km_per_liter_simple, 2.7), 0.1);
  const kmLfull = Math.max(num(input.km_per_liter_full, 2.2), 0.1);

  const dieselPrice = num(input.diesel_price_per_liter, 25);
  const currency = input.currency || 'MXN';
  const exchangeRate = Math.max(num(input.mxn_usd_exchange_rate, 17), 0.1);

  const tireCost = num(input.average_tire_cost, 8500);
  const tireLifeKm = Math.max(num(input.average_tire_life_km, 120000), 1);
  const monthlyTireCostInput = num(input.estimated_monthly_tire_cost, 0);
  const monthlyTireCostAuto = tireCost * safeDiv(kmPerUnitMonth, tireLifeKm);
  const monthlyTireCost = monthlyTireCostInput > 0 ? monthlyTireCostInput : monthlyTireCostAuto;

  const hasRepairs = Boolean(input.has_frequent_repairs);
  const repairCostTrip = num(input.repair_cost_per_trip, 0);
  const tripsPerMonth = num(input.trips_per_month, 0);

  const additionalTrips = num(input.additional_trips_per_month, 0);
  const profitSimple = num(input.profit_per_simple_trip, 0);
  const profitFull = num(input.profit_per_full_trip, 0);

  const monthlyBlowouts = num(input.estimated_monthly_blowouts, 0);
  const monthlyDefective = num(input.estimated_monthly_defective_tires, 0);
  const blowoutCost = num(input.average_blowout_cost, 0);
  const truckDamageCost = num(input.average_truck_damage_cost, 0);
  const thirdPartyCost = num(input.average_third_party_incident_cost, 0);

  const fuelEfficiencyLossPct = psiDifference * assumptions.dieselInefficiencyPerPsiPct / 100;
  const weightedEfficiency = safeDiv((simpleUnits * kmLsimple) + (fullUnits * kmLfull), Math.max(tractos, 1));
  const monthlyKmTotal = tractos * kmPerUnitMonth;
  const baselineLiters = safeDiv(monthlyKmTotal, weightedEfficiency);
  const extraLitersMonth = baselineLiters * fuelEfficiencyLossPct;

  const dieselOvercostMonth = extraLitersMonth * dieselPrice;
  const dieselOvercostYear = dieselOvercostMonth * 12;

  const wearLossPct = Math.min(psiDifference * assumptions.tireWearPerPsiPct / 100, 0.5);
  const tireWearLossMonth = monthlyTireCost * totalTires * wearLossPct;
  const tireWearLossYear = tireWearLossMonth * 12;

  const talachaMonth = hasRepairs ? (repairCostTrip * tripsPerMonth) : 0;
  const talachaYear = talachaMonth * 12;

  const weightedProfit = safeDiv((profitSimple * simpleUnits) + (profitFull * fullUnits), Math.max(tractos, 1));
  const productivityBenefitMonth = additionalTrips * weightedProfit;
  const productivityBenefitYear = productivityBenefitMonth * 12;

  const incidentCostMonth =
    (monthlyBlowouts * (blowoutCost + truckDamageCost + thirdPartyCost))
    + (monthlyDefective * (blowoutCost * 0.45));
  const incidentCostYear = incidentCostMonth * 12;

  const hiddenCostMonth = dieselOvercostMonth + tireWearLossMonth + talachaMonth + incidentCostMonth;
  const hiddenCostYear = hiddenCostMonth * 12;

  const investmentUsd = (simpleUnits * assumptions.simpleInvestmentUsd) + (fullUnits * assumptions.fullInvestmentUsd);
  const monthlyFeeUsd = (simpleUnits * assumptions.simpleMonthlyUsd) + (fullUnits * assumptions.fullMonthlyUsd);

  const investmentInitial = toCurrency(investmentUsd, currency, exchangeRate);
  const monthlySubscription = toCurrency(monthlyFeeUsd, currency, exchangeRate);

  const potentialSavingMonth = hiddenCostMonth + productivityBenefitMonth - monthlySubscription;
  const potentialSavingYear = potentialSavingMonth * 12;

  const paybackMonths = potentialSavingMonth > 0 ? safeDiv(investmentInitial, potentialSavingMonth) : 999;
  const roiAnnual = safeDiv(potentialSavingYear, Math.max(investmentInitial, 1e-6));
  const estimatedReturnMonthly = safeDiv(potentialSavingMonth, Math.max(investmentInitial, 1e-6));
  const estimatedReturnAnnual = safeDiv(potentialSavingYear, Math.max(investmentInitial, 1e-6));
  const estimatedTirMonthly = Math.max(((1 + Math.max(roiAnnual, 0)) ** (1 / 12)) - 1, 0);

  const withoutAnnual = {
    fuel: dieselOvercostYear,
    tires: tireWearLossYear,
    repairs: talachaYear,
    productivity: productivityBenefitYear,
    incidents: incidentCostYear,
  };
  const withAnnual = {
    fuel: dieselOvercostYear * assumptions.withApsFactor,
    tires: tireWearLossYear * assumptions.withApsFactor,
    repairs: talachaYear * assumptions.withApsFactor,
    productivity: 0,
    incidents: incidentCostYear * assumptions.withApsFactor,
  };

  const withoutTotal = Object.values(withoutAnnual).reduce((acc, val) => acc + val, 0);
  const withTotal = Object.values(withAnnual).reduce((acc, val) => acc + val, 0) + (monthlySubscription * 12);
  const operationalReductionPct = safeDiv(Math.max(withoutTotal - withTotal, 0), Math.max(withoutTotal, 1e-6)) * 100;

  const breakEven = [];
  let cumulative = -investmentInitial;
  for (let month = 1; month <= 84; month += 1) {
    cumulative += potentialSavingMonth;
    breakEven.push({
      month,
      cumulative: round(cumulative, 2),
      is_break_even: cumulative >= 0,
    });
  }

  const savings7y = potentialSavingYear * assumptions.sensorLifetimeYears;

  return {
    currency,
    exchange_rate: round(exchangeRate, 4),
    assumptions,
    fleet: {
      tractos: round(tractos, 2),
      simple_units: round(simpleUnits, 2),
      full_units: round(fullUnits, 2),
      auto_total_tires: round(autoTotalTires, 2),
      total_tires: round(totalTires, 2),
    },
    psi_difference: round(psiDifference, 2),
    fuel_efficiency_loss_pct: round(fuelEfficiencyLossPct * 100, 3),
    extra_liters_month: round(extraLitersMonth, 2),
    diesel_overcost_month: round(dieselOvercostMonth, 2),
    diesel_overcost_year: round(dieselOvercostYear, 2),
    tire_wear_loss_month: round(tireWearLossMonth, 2),
    tire_wear_loss_year: round(tireWearLossYear, 2),
    talacha_month: round(talachaMonth, 2),
    talacha_year: round(talachaYear, 2),
    productivity_benefit_month: round(productivityBenefitMonth, 2),
    productivity_benefit_year: round(productivityBenefitYear, 2),
    incident_cost_month: round(incidentCostMonth, 2),
    incident_cost_year: round(incidentCostYear, 2),
    hidden_cost_month: round(hiddenCostMonth, 2),
    hidden_cost_year: round(hiddenCostYear, 2),
    potential_saving_month: round(potentialSavingMonth, 2),
    potential_saving_year: round(potentialSavingYear, 2),
    investment_initial: round(investmentInitial, 2),
    monthly_subscription: round(monthlySubscription, 2),
    payback_months: round(paybackMonths, 2),
    roi_annual: round(roiAnnual, 4),
    estimated_return_monthly: round(estimatedReturnMonthly, 4),
    estimated_return_annual: round(estimatedReturnAnnual, 4),
    estimated_tir_monthly: round(estimatedTirMonthly, 4),
    estimated_tir_annual: round(Math.max(roiAnnual, 0), 4),
    reduction_wear_pct: round(Math.min(wearLossPct * 100, 100), 2),
    operational_reduction_pct: round(Math.max(operationalReductionPct, 0), 2),
    annual_comparison: {
      without_aps: {
        ...Object.fromEntries(Object.entries(withoutAnnual).map(([k, v]) => [k, round(v, 2)])),
        total: round(withoutTotal, 2),
      },
      with_aps: {
        ...Object.fromEntries(Object.entries(withAnnual).map(([k, v]) => [k, round(v, 2)])),
        total: round(withTotal, 2),
      },
    },
    break_even: breakEven,
    savings_7y: round(savings7y, 2),
  };
};

export const defaultClientCalculatorState = {
  name: '',
  company: '',
  email: '',
  phone: '',
  city: '',
  state: '',
  country: 'México',
  comments: '',
  tractos: 50,
  trailers_2axle_8tires: 20,
  trailers_3axle_12tires: 15,
  configuration_type: 'Mixto',
  full_units_percentage: 40,
  total_tires: 0,
  rim_type: 'Mixto',
  steel_rims_count: 480,
  aluminum_rims_count: 220,
  recommended_psi: 105,
  actual_avg_psi: 96,
  monthly_km_per_unit: 15000,
  km_per_liter_simple: 2.8,
  km_per_liter_full: 2.2,
  diesel_price_per_liter: 25.5,
  currency: 'MXN',
  mxn_usd_exchange_rate: 17,
  average_tire_cost: 8500,
  average_tire_life_km: 120000,
  estimated_monthly_tire_cost: 0,
  monitors_tire_pressure: false,
  has_frequent_repairs: true,
  average_km_per_trip: 850,
  repair_cost_per_trip: 2300,
  trips_per_month: 38,
  additional_trips_per_month: 8,
  profit_per_simple_trip: 12000,
  profit_per_full_trip: 21000,
  estimated_monthly_blowouts: 2,
  estimated_monthly_defective_tires: 6,
  average_blowout_cost: 14000,
  average_truck_damage_cost: 42000,
  average_third_party_incident_cost: 80000,
  request_assessment: false,
  honeypot: '',
};
